import React, { useEffect, useState } from 'react';
import PrinterSelectionComponent from '../Step1_PrinterSelection/PrinterSelectionComponent/PrinterSelectionComponent';
import UpgradeReview from '../Step2_Review&Checkout/UpgradeReview';
import UpgradeConfirmation from '../Step3_Confirmation/UpgradeConfirmation/UpgradeConfirmation';
import { StyledPage, ErrorCardWrapper } from './styles';
import useSubscriptionDataFetcher from '../../hooks/useSubscriptionDataFetcher';
import useSubscriptionId from '../../hooks/useSubscriptionId';
import useShellContext from '../../hooks/useShellContext/useShellContext';
import useFlags from '../../hooks/useFlags';
import { useLazyGetProductDetails } from 'src/services/ApolloClient';
import {
  buildPrinterList,
  PrinterItem
} from 'src/utils/Products/productFilter';
import { getActivePaperSubscription } from '../../helpers/paperHelper';
import { LoadingHandler } from '../LoadingHandler';
import getEnvironmentVariables from 'src/helpers/getEnvironmentVariables';
import { getStack } from 'src/helpers/getStack';
import { SubscriptionEntityTypeEnum } from 'src/types/subscription';
import useSubscriptionOptions from 'src/hooks/useSubscriptionOptions/useSubscriptionOptions';
import ErrorCard from '../ErrorCard';

const UpgradeRootComponent = (): JSX.Element => {
  const [currentStep, setCurrentStep] = useState<
    'step1' | 'step2' | 'confirmation'
  >('step1');
  const [isDeviceDataLoading, setIsDeviceDataLoading] = useState<boolean>(true);
  const [isDeviceError, setIsDeviceError] = useState<boolean>(false);

  const { authProvider, stack } = useShellContext();
  const env = getEnvironmentVariables(getStack(stack));
  const bizlogicId = env.variables.BL_ID;
  const { flags } = useFlags();
  const initialSkuArray = flags?.PF_Upgrade_SKUs || [];
  const [skuArray, setSkuArray] = useState<string[]>(initialSkuArray);
  const [inkProductSku, setInkProductSku] = useState<string>('');

  const [getProductDetails] = useLazyGetProductDetails();
  const subscriptionId = useSubscriptionId();
  const { subscriptionData, subLoading, subError } = useSubscriptionDataFetcher(
    authProvider,
    stack,
    subscriptionId
  );
  const {
    loading: optionsLoading,
    error: optionsError,
    optionsData: optionsData,
    fetchSubscriptionOptions
  } = useSubscriptionOptions(authProvider, stack, bizlogicId, subscriptionId);

  const [nonCurrentPrinterDetails, setNonCurrentPrinterDetails] = useState<
    PrinterItem[] | null
  >(null);

  const [currentPrinterDetails, setCurrentPrinterDetails] = useState<
    PrinterItem[] | null
  >(null);

  const [selectedProductSKU, setSelectedProductSKU] = useState<string | null>(
    null
  );
  const [orderNumber, setOrderNumber] = useState<string>('');
  useEffect(() => {
    if (!subscriptionData) return;
    const allPrinters = subscriptionData?.entities?.filter(
      (entity) => entity.entityType === SubscriptionEntityTypeEnum.PRINTER
    );

    const allInk = subscriptionData?.entities?.filter(
      (entity) => entity.entityType === SubscriptionEntityTypeEnum.INSTANT_INK
    );
    const inkData = allInk?.[0];
    const inkProductSku = inkData?.product?.value?.productSku;
    setInkProductSku(inkProductSku || '');
    const printerData = allPrinters?.[0];
    const parentSku = printerData?.product?.value?.parentProductSku;

    if (parentSku) {
      setSkuArray((prev) => {
        const merged = [parentSku, ...prev, ...initialSkuArray].filter(
          (sku) => sku.trim() !== ''
        );
        return merged;
      });
    }
  }, [subscriptionData]);

  const subscriptions = subscriptionData ? [subscriptionData] : [];

  let activePaperSubscription = false;
  if (subscriptionData?.entities) {
    activePaperSubscription = getActivePaperSubscription(
      subscriptionData.entities
    );
  }
  useEffect(() => {
    fetchSubscriptionOptions();
  }, []);
  useEffect(() => {
    const fetchProductDetails = async () => {
      setIsDeviceDataLoading(true);

      /* istanbul ignore next */
      await getProductDetails({
        variables: {
          parentSKU: skuArray
        },
        onError: () => {
          setIsDeviceError(true);
          setIsDeviceDataLoading(false);
        },
        onCompleted: (response) => {
          const result = buildPrinterList(
            response,
            skuArray,
            activePaperSubscription,
            optionsData,
            inkProductSku,
            flags?.PF_RecommendedPrinterUpgradeRules
          );
          setNonCurrentPrinterDetails(result.nonCurrentPrinters);
          setCurrentPrinterDetails(result.currentPrinterList);
          setIsDeviceDataLoading(false);
        }
      });
    };
    if (skuArray.length > 0 && !!optionsData) {
      fetchProductDetails();
    }
  }, [getProductDetails, skuArray, optionsData]);
  const handlePrinterSelection = (productSKU?: string) => {
    if (productSKU) {
      setSelectedProductSKU(productSKU);
    }
    setCurrentStep('step2');
  };
  const selectedPrinterDetails = nonCurrentPrinterDetails?.find(
    (printer) => printer.productSKU === selectedProductSKU
  );

  const handleBackToSelection = () => {
    setCurrentStep('step1');
  };

  const handleUpgradeConfirm = () => {
    setCurrentStep('confirmation');
  };
  const isLoading = subLoading || isDeviceDataLoading || optionsLoading;
  const error = subError || isDeviceError || optionsError;

  if (error) {
    return (
      <ErrorCardWrapper>
        <ErrorCard />
      </ErrorCardWrapper>
    );
  } else if (isLoading) {
    return <LoadingHandler loading={isLoading} />;
  } else {
    return (
      <StyledPage>
        {currentStep === 'step1' && nonCurrentPrinterDetails && (
          <PrinterSelectionComponent
            selectedPrinterSku={handlePrinterSelection}
            nonCurrentPrinterDetails={nonCurrentPrinterDetails}
            currentPrinterDetails={currentPrinterDetails}
          />
        )}
        {currentStep === 'step2' && (
          <UpgradeReview
            onBack={handleBackToSelection}
            onConfirm={handleUpgradeConfirm}
            subscriptions={subscriptions}
            selectedPrinterDetails={selectedPrinterDetails}
            currentPrinterDetails={currentPrinterDetails}
            setOrderNumber={setOrderNumber}
          />
        )}
        {currentStep === 'confirmation' && (
          <UpgradeConfirmation
            orderNumber={orderNumber}
            selectedPrinterDetails={selectedPrinterDetails}
          />
        )}
      </StyledPage>
    );
  }
};

export default UpgradeRootComponent;
